import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';

interface CartItem {
    id: number;
    service_id: number;
    name: string;
    quantity: number;
    unit_price: number;
}

interface Cart {
    item_count: number;
    subtotal: number;
    tax: number;
    total: number;
}

export default function CartPage() {
    const [cart, setCart] = useState<Cart | null>(null);
    const [items, setItems] = useState<CartItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadCart();
    }, []);

    const loadCart = async () => {
        try {
            setLoading(true);
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/cart'), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            setCart(data.cart);
            setItems(data.items);
        } catch (error) {
            console.error('Load cart error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
        if (newQuantity === 0) {
            await handleRemove(itemId);
            return;
        }

        try {
            const { getApiUrl } = await import('../config');
            await fetch(getApiUrl(`/cart/items/${itemId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ quantity: newQuantity })
            });
            loadCart();
        } catch (error) {
            console.error('Update quantity error:', error);
        }
    };

    const handleRemove = async (itemId: number) => {
        try {
            const { getApiUrl } = await import('../config');
            await fetch(getApiUrl(`/cart/items/${itemId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            loadCart();
        } catch (error) {
            console.error('Remove item error:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2">
                        {items.length === 0 ? (
                            <div className="bg-white rounded-lg shadow p-8 text-center">
                                <ShoppingBag size={48} className="mx-auto text-gray-400 mb-4" />
                                <p className="text-gray-600 mb-4">Your cart is empty</p>
                                <Link to="/services/listing" className="text-blue-600 hover:text-blue-700 font-medium">
                                    Continue Shopping
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {items.map((item) => (
                                    <div key={item.id} className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-900">{item.name}</h3>
                                            <p className="text-gray-600">£{parseFloat(item.unit_price).toFixed(2)}</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                >
                                                    <Minus size={16} />
                                                </button>
                                                <span className="w-8 text-center">{item.quantity}</span>
                                                <button
                                                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                                    className="p-1 hover:bg-gray-100 rounded"
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                            <div className="w-24 text-right font-semibold">
                                                £{(item.unit_price * item.quantity).toFixed(2)}
                                            </div>
                                            <button
                                                onClick={() => handleRemove(item.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Summary */}
                    {items.length > 0 && cart && (
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
                                <div className="space-y-3 mb-4">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="font-medium">£{cart.subtotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Tax (20%)</span>
                                        <span className="font-medium">£{cart.tax.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t pt-3 flex justify-between">
                                        <span className="font-semibold text-gray-900">Total</span>
                                        <span className="text-xl font-bold text-blue-600">£{cart.total.toFixed(2)}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate('/checkout')}
                                    className="w-full bg-blue-600 text-white py-2 rounded font-medium hover:bg-blue-700 transition-colors"
                                >
                                    Proceed to Checkout
                                </button>
                                <Link
                                    to="/services/listing"
                                    className="block text-center mt-3 text-blue-600 hover:text-blue-700 text-sm"
                                >
                                    Continue Shopping
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
